/**
 * Plain-object `sx` shape only — deliberately narrower than MUI's `SxProps<Theme>`, which also
 * permits a function or an array of style objects. `DataTable`'s row-style merge logic (spreading
 * and indexing into the returned value to combine it with highlight styling) only handles a plain
 * object correctly; a function or array form would silently be merged wrong.
 */
type RowSx = Record<string, unknown>;

export interface ColumnConfig<T> {
  key?: keyof T | string;
  label: string;
  width?: string | number;
  render?: (row: T) => React.ReactNode;
  editable?: boolean;
  onChange?: (id: number, value: any) => void;
  parseValue?: (value: string) => any;
  type?: ColumnType;
  // For select editors
  options?: Array<{ value: any; label: string }>;
}

export interface DataTableProps<T> {
  columns: ColumnConfig<T>[];
  data: T[];
  tableLayout?: 'auto' | 'fixed';
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onEdit?: (id: number) => void;
  getRowId: (row: T) => number;
  page?: number;
  rowsPerPage?: number;
  totalCount?: number;
  onPageChange?: (newPage: number) => void;
  onRowsPerPageChange?: (newSize: number) => void;
  selected?: number[];
  onSelectionChange?: (selectedIds: number[]) => void;
  onInlineSave?: (row: T) => void | Promise<void>;
  editingRowId?: number | null;
  setEditingRowId?: (id: number | null) => void;
  editedRow?: Partial<T>;
  setEditedRow?: (row: Partial<T>) => void;
  emptyMessage?: string;
  renderCustomActions?: (row: T) => React.ReactNode;
  highlightedRowId?: number | null;
  /**
   * When provided, rows for which this returns `false` hide both the selection checkbox and the
   * generic `onEdit` action — a row that cannot be bulk-acted-on (e.g. already cancelled) is also
   * treated as non-editable via this table's own "Edytuj" action.
   */
  isRowSelectable?: (row: T) => boolean;
  /** Optional per-row style (e.g. greyed-out inactive rows). Highlight styling always takes precedence when both apply. */
  getRowSx?: (row: T) => RowSx | undefined;
}

export enum ColumnType {
  text = 'text',
  number = 'number',
  checkbox = 'checkbox',
  select = 'select',
  date = 'date',
  wysiwyg = 'wysiwyg',
  time = 'time',
}
